/* Chat del canale come su Discord: tutti i messaggi (anche i vecchi), elimina un messaggio o svuota tutto il canale. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, FileText, Hash, Loader2, MessagesSquare, Pin, RefreshCw, Trash2, X } from "lucide-react";
import { apiError, botDelete, botGet, botPost } from "@/lib/api";
import { DiscordMarkdown } from "@/components/DiscordMarkdown";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { outlineBtn } from "@/components/kit";
import { InfoTip } from "@/pages/tickets/ticketsKit";

const PAGE = 50;
const chat = {
  page: (id, params) => botGet(`channels/${id}/messages`, { limit: PAGE, ...params }),
  remove: (id, mid) => botDelete(`channels/${id}/messages/${mid}`),
  purge: (id) => botPost(`channels/${id}/purge`, {}),
  job: (jid) => botGet(`purge-jobs/${jid}`),
  cancel: (jid) => botDelete(`purge-jobs/${jid}`),
};
const CHAT_TYPES = ["text", "announcement", "voice", "stage"];
const byId = (a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1);
const hex = (n) => (typeof n === "number" ? `#${n.toString(16).padStart(6, "0")}` : "#202225");
const time = (iso) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? `Oggi alle ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
};
const SYSTEM_TEXT = { 7: "è entrato nel server 👋", 8: "ha potenziato il server 💎", 9: "ha potenziato il server 💎", 10: "ha potenziato il server 💎", 11: "ha potenziato il server 💎", 6: "ha fissato un messaggio 📌" };

/** Embed nel formato di Discord (come arriva dal bot). */
function Embed({ e }) {
  return (
    <div className="mt-1.5 max-w-[480px] rounded-[4px] bg-[#2B2D31] py-2 pl-3 pr-3" style={{ borderLeft: `4px solid ${hex(e.color)}` }}>
      <div className="flex gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          {e.author?.name && <div className="flex items-center gap-2">{e.author.icon_url && <img src={e.author.icon_url} alt="" className="h-5 w-5 rounded-full" />}<span className="text-xs font-semibold text-white">{e.author.name}</span></div>}
          {e.title && <div className="text-sm font-semibold text-white"><DiscordMarkdown text={e.title} /></div>}
          {e.description && <DiscordMarkdown text={e.description} className="text-[13px] text-[#DBDEE1]" />}
          {e.fields?.length > 0 && (
            <div className="grid gap-2 pt-0.5" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
              {e.fields.map((f, i) => (
                <div key={i} style={{ gridColumn: f.inline ? "span 1" : "1 / -1" }} className="min-w-0">
                  <div className="text-xs font-semibold text-white"><DiscordMarkdown text={f.name} /></div>
                  <DiscordMarkdown text={f.value} className="text-xs text-[#DBDEE1]" />
                </div>
              ))}
            </div>
          )}
          {e.image?.url && <img src={e.image.url} alt="" className="mt-1 max-h-60 rounded" />}
          {e.footer?.text && <div className="flex items-center gap-1.5 pt-1 text-[11px] text-[#949BA4]">{e.footer.icon_url && <img src={e.footer.icon_url} alt="" className="h-4 w-4 rounded-full" />}{e.footer.text}</div>}
        </div>
        {e.thumbnail?.url && <img src={e.thumbnail.url} alt="" className="h-16 w-16 shrink-0 rounded object-cover" />}
      </div>
    </div>
  );
}

function Message({ m, grouped, onDelete, deleting }) {
  const content = (m.content || "").replace(/<@!?(\d+)>/g, (x, id) => (m.mentions?.[id] ? `@${m.mentions[id]}` : x));
  if (m.system && SYSTEM_TEXT[m.type]) {
    return <div className="flex items-center gap-2 px-4 py-1 text-[13px] text-[#949BA4]"><span className="w-10 text-center">→</span><b className="text-white">{m.author?.displayName}</b> {SYSTEM_TEXT[m.type]}<span className="text-[11px]">{time(m.createdAt)}</span></div>;
  }
  return (
    <div className={`group relative flex gap-3 px-4 hover:bg-[#2e3035] ${grouped ? "py-0.5" : "mt-3 pt-1 pb-0.5"}`}>
      <div className="w-10 shrink-0">
        {!grouped && (m.author?.avatar ? <img src={m.author.avatar} alt="" className="h-10 w-10 rounded-full" /> : <div className="h-10 w-10 rounded-full bg-[#5865F2]" />)}
      </div>
      <div className="min-w-0 flex-1">
        {!grouped && (
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-[15px] font-medium" style={{ color: m.author?.color || "#F2F3F5" }}>{m.author?.displayName || m.author?.username}</span>
            {m.author?.bot && <span className="rounded-[3px] bg-[#5865F2] px-1 text-[10px] font-semibold text-white">APP</span>}
            <span className="text-[11px] text-[#949BA4]">{time(m.createdAt)}</span>
            {m.pinned && <Pin className="h-3 w-3 text-[#949BA4]" />}
          </div>
        )}
        {m.replyTo && !grouped && <p className="text-[11px] text-[#949BA4]">↪ risposta a un messaggio</p>}
        {content && <DiscordMarkdown text={content} className="text-[15px] leading-[1.375rem] text-[#DBDEE1]" />}
        {m.editedAt && <span className="text-[10px] text-[#949BA4]">(modificato)</span>}
        {m.embeds?.map((e, i) => <Embed key={i} e={e} />)}
        {m.attachments?.map((a) => (/^image\//.test(a.contentType || "")
          ? <a key={a.url} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={a.name} className="mt-1.5 max-h-72 max-w-full rounded-lg" /></a>
          : <a key={a.url} href={a.url} target="_blank" rel="noreferrer" className="mt-1.5 flex w-fit items-center gap-2 rounded-lg border border-[#232838] bg-[#2B2D31] px-3 py-2 text-sm text-[#00A8FC] hover:underline"><FileText className="h-4 w-4" />{a.name}</a>))}
        {m.stickers?.map((s) => <img key={s.url} src={s.url} alt={s.name} className="mt-1 h-28 w-28" />)}
        {m.buttons?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">{m.buttons.map((b, i) => <span key={i} className="rounded bg-[#4E5058] px-3 py-1 text-xs font-medium text-white">{b.emoji ? `${b.emoji} ` : ""}{b.label}</span>)}</div>
        )}
      </div>
      <button type="button" title="Elimina questo messaggio" disabled={deleting} onClick={() => onDelete(m)}
        className="absolute right-3 top-0 -translate-y-1/2 rounded-md border border-[#1f2023] bg-[#313338] p-1.5 text-[#F23F43] opacity-0 shadow transition-opacity hover:bg-[#F23F43]/15 group-hover:opacity-100">
        {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

/** Finestra per svuotare il canale, con avanzamento. */
function PurgeDialog({ open, onOpenChange, channel, onFinished }) {
  const [confirm, setConfirm] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => { if (open) { setConfirm(""); setJob(null); } return () => clearInterval(timer.current); }, [open]);
  const start = async () => {
    try {
      const j = await chat.purge(channel.id);
      setJob(j);
      timer.current = setInterval(async () => {
        try {
          const x = await chat.job(j.id);
          setJob(x);
          if (x.status !== "running") {
            clearInterval(timer.current);
            toast.success(`#${channel.name}: ${x.deleted} messaggi eliminati`);
            onFinished();
          }
        } catch { clearInterval(timer.current); }
      }, 1200);
    } catch (e) { toast.error(apiError(e)); }
  };
  const running = job?.status === "running";
  return (
    <Dialog open={open} onOpenChange={(o) => (running ? null : onOpenChange(o))}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Elimina tutti i messaggi di #{channel.name}</DialogTitle>
          <DialogDescription className="text-[#949BA4]">Il canale resta (stesso nome, permessi e posizione), ma tutti i messaggi vengono cancellati per sempre, anche quelli vecchi e quelli fissati.</DialogDescription>
        </DialogHeader>
        {job ? (
          <div className="space-y-2 rounded-xl border border-[#5865F2]/40 bg-[#5865F2]/10 p-3 text-sm">
            <p className="flex items-center gap-2 font-semibold text-white">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : "✓"} {running ? "Sto eliminando…" : job.status === "cancelled" ? "Interrotto" : "Fatto!"}</p>
            <p>Messaggi eliminati: <b className="text-white">{job.deleted}</b>{job.skipped ? ` · saltati: ${job.skipped}` : ""}</p>
            {running && job.oldPhase && <p className="text-xs text-[#FEE75C]">Ora sto eliminando i messaggi più vecchi di 14 giorni: Discord li fa cancellare uno alla volta, ci vuole un po'. Puoi chiudere la pagina, il bot continua da solo.</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="flex gap-2 rounded-xl border border-[#F23F43]/40 bg-[#F23F43]/10 p-3 text-sm text-[#ffc1c2]"><AlertTriangle className="h-4 w-4 shrink-0" />Non si può annullare. Se ti servono, prima salva i messaggi importanti.</p>
            <p className="text-sm">Per confermare scrivi <b className="text-white">ELIMINA</b>:</p>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} className="h-9 w-full rounded-md border border-[#232838] bg-[#131620] px-3 text-sm text-white outline-none focus:border-[#F23F43]" placeholder="ELIMINA" data-testid="purge-confirm" />
          </div>
        )}
        <DialogFooter>
          {running ? <Button variant="outline" onClick={() => chat.cancel(job.id).catch(() => {})} className={outlineBtn}><X className="mr-1.5 h-4 w-4" />Interrompi</Button>
            : job ? <Button onClick={() => onOpenChange(false)} className="bg-[#5865F2] text-white hover:bg-[#4752C4]">Chiudi</Button>
            : <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className={outlineBtn}>Annulla</Button>
              <Button disabled={confirm !== "ELIMINA"} onClick={start} className="bg-[#F23F43] font-semibold text-white hover:bg-[#d83236]" data-testid="purge-start"><Trash2 className="mr-1.5 h-4 w-4" />Elimina tutto</Button>
            </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ChannelChat({ channel }) {
  const [msgs, setMsgs] = useState(null);
  const [err, setErr] = useState(null);
  const [older, setOlder] = useState(true);
  const [loadingOld, setLoadingOld] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [purge, setPurge] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const box = useRef(null);
  const keepScroll = useRef(null);
  const canChat = channel?.id && CHAT_TYPES.includes(channel.type);

  const toBottom = () => { const b = box.current; if (b) b.scrollTop = b.scrollHeight; };
  const loadLatest = useCallback(async () => {
    if (!canChat) return;
    try {
      const list = await chat.page(channel.id);
      setMsgs(list.sort(byId)); setOlder(list.length >= PAGE); setErr(null);
      requestAnimationFrame(toBottom);
    } catch (e) { setErr(apiError(e)); setMsgs([]); }
  }, [channel?.id, canChat]);

  useEffect(() => { setMsgs(null); setErr(null); loadLatest(); }, [loadLatest]);

  // Nuovi messaggi: controlla ogni 8 secondi (solo se la pagina è visibile).
  useEffect(() => {
    if (!canChat) return undefined;
    const t = setInterval(async () => {
      if (document.hidden || !msgs?.length) return;
      try {
        const newest = msgs[msgs.length - 1].id;
        const list = await chat.page(channel.id, { after: newest });
        if (list.length) {
          const stick = atBottom;
          setMsgs((m) => [...m, ...list.filter((x) => !m.some((y) => y.id === x.id))].sort(byId));
          if (stick) requestAnimationFrame(toBottom);
        }
      } catch { /* riprova al giro dopo */ }
    }, 8000);
    return () => clearInterval(t);
  }, [canChat, channel?.id, msgs, atBottom]);

  const loadOlder = async () => {
    if (!msgs?.length || loadingOld) return;
    setLoadingOld(true);
    try {
      const list = await chat.page(channel.id, { before: msgs[0].id });
      keepScroll.current = box.current.scrollHeight - box.current.scrollTop;
      setMsgs((m) => [...list, ...m].sort(byId));
      setOlder(list.length >= PAGE);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingOld(false); }
  };
  // dopo aver aggiunto i vecchi messaggi in cima, la vista resta dov'era
  useLayoutEffect(() => {
    if (keepScroll.current != null && box.current) { box.current.scrollTop = box.current.scrollHeight - keepScroll.current; keepScroll.current = null; }
  }, [msgs]);

  const onScroll = () => {
    const b = box.current;
    if (!b) return;
    setAtBottom(b.scrollHeight - b.scrollTop - b.clientHeight < 60);
    if (b.scrollTop < 40 && older) loadOlder();
  };
  const removeOne = async (m) => {
    setDeleting(m.id);
    try { await chat.remove(channel.id, m.id); setMsgs((list) => list.filter((x) => x.id !== m.id)); toast.success("Messaggio eliminato"); } catch (e) { toast.error(apiError(e)); } finally { setDeleting(null); }
  };

  if (!channel?.id) return <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed border-[#232838] p-6 text-center text-sm text-[#949BA4]">Salva il canale su Discord per vedere la chat.</div>;
  if (!canChat) return <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed border-[#232838] p-6 text-center text-sm text-[#949BA4]">Questo tipo di canale non ha una chat da mostrare.</div>;

  return (
    <div className="flex h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-xl border border-[#1f2023] bg-[#313338]" data-testid="channel-chat">
      <div className="flex items-center gap-2 border-b border-[#1f2023] px-3 py-2.5">
        <Hash className="h-5 w-5 text-[#80848E]" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{channel.name}</span>
        <InfoTip text="La chat vera del canale su Discord, con tutti i messaggi. Scorri in alto per caricare quelli più vecchi. Passa sopra a un messaggio per eliminarlo, oppure svuota tutto il canale col tasto rosso. I nuovi messaggi compaiono da soli." />
        <button type="button" title="Aggiorna" onClick={loadLatest} className="rounded-md p-1.5 text-[#B5BAC1] hover:bg-white/5 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
        <button type="button" onClick={() => setPurge(true)} disabled={!msgs?.length} className="flex items-center gap-1 rounded-md bg-[#F23F43]/15 px-2 py-1 text-xs font-semibold text-[#ff9a9c] hover:bg-[#F23F43]/25 disabled:opacity-40" data-testid="purge-open">
          <Trash2 className="h-3.5 w-3.5" />Elimina tutti
        </button>
      </div>
      <div ref={box} onScroll={onScroll} className="relative flex-1 overflow-y-auto pb-3">
        {msgs === null && <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#949BA4]" /></div>}
        {err && <p className="m-4 rounded-lg bg-[#F23F43]/10 p-3 text-sm text-[#ffb3b4]">{err}</p>}
        {msgs?.length === 0 && !err && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-[#949BA4]"><MessagesSquare className="h-10 w-10 opacity-50" /><p className="text-sm">Nessun messaggio in questo canale.</p></div>
        )}
        {msgs?.length > 0 && (
          <div className="py-2 text-center">
            {older ? <button type="button" onClick={loadOlder} disabled={loadingOld} className="text-xs font-semibold text-[#00A8FC] hover:underline">{loadingOld ? "Carico…" : "Carica messaggi più vecchi"}</button>
              : <p className="text-xs text-[#949BA4]">— Inizio del canale #{channel.name} —</p>}
          </div>
        )}
        {msgs?.map((m, i) => {
          const prev = msgs[i - 1];
          const grouped = prev && !prev.system && !m.system && prev.author?.id === m.author?.id && new Date(m.createdAt) - new Date(prev.createdAt) < 7 * 60000 && !m.replyTo;
          return <Message key={m.id} m={m} grouped={grouped} onDelete={removeOne} deleting={deleting === m.id} />;
        })}
      </div>
      {!atBottom && msgs?.length > 0 && (
        <button type="button" onClick={toBottom} className="mx-auto -mt-12 mb-3 flex items-center gap-1 rounded-full bg-[#5865F2] px-3 py-1 text-xs font-semibold text-white shadow-lg"><ArrowDown className="h-3.5 w-3.5" />Ultimi messaggi</button>
      )}
      <PurgeDialog open={purge} onOpenChange={setPurge} channel={channel} onFinished={loadLatest} />
    </div>
  );
}
